import type { JSX } from 'preact';
import { useEffect, useLayoutEffect, useRef, useState } from 'preact/hooks';
import { ChevronLeft, ChevronRight, Pencil } from 'lucide-preact';
import { boatDisplayName, type BoatDocument } from '../../sim/boat/index.ts';
import {
  LIVE_BOT_LEVELS,
  type SeatController,
} from '../../sim/bot/seats.ts';
import {
  MAX_COMPETITOR_NAME_LENGTH,
  sanitizeCompetitorName,
} from '../play/competitorNames.ts';
import type { BotProfileKnobs } from '../../sim/botProfile/types.ts';
import { BoatSideArt } from '../boats/sideArt.tsx';
import { startNavProps } from './keyboard.ts';

export type BoatSelectChoice = {
  id: string;
  boat: BoatDocument;
};

/** Centre card in the row (Skiff when there are three). */
export function middleBoatId(boats: readonly BoatSelectChoice[]): string | null {
  if (boats.length === 0) {
    return null;
  }

  return boats[Math.floor(boats.length / 2)]?.id ?? null;
}

function formatMeasure(value: number, unit: string): string {
  const rounded = Math.round(value * 10) / 10;
  const text = Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
  return `${text} ${unit}`;
}

function Stat({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div class="boat-select-stat">
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}

function BoatStats({ boat }: { boat: BoatDocument }): JSX.Element {
  return (
    <dl class="boat-select-stats">
      <Stat label="Length" value={formatMeasure(boat.lengthM, 'm')} />
      <Stat label="Beam" value={formatMeasure(boat.beamM, 'm')} />
      <Stat label="Draft" value={formatMeasure(boat.draftM, 'm')} />
      <Stat label="Planing speed" value={formatMeasure(boat.planingSpeedKmh, 'km/h')} />
      <Stat label="Turn" value={formatMeasure(boat.turnM, 'm')} />
      <Stat label="Accelerate" value={formatMeasure(boat.accelM, 'm')} />
      <Stat label="Brake" value={formatMeasure(boat.brakeM, 'm')} />
    </dl>
  );
}

function sameName(left: string, right: string): boolean {
  return left.toLocaleLowerCase() === right.toLocaleLowerCase();
}

function RacerName({
  name,
  taken,
  onCommit,
}: {
  name: string;
  taken: readonly string[];
  onCommit: (name: string) => void;
}): JSX.Element {
  const input = useRef<HTMLInputElement>(null);
  const ignoreBlur = useRef(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(name);

  useEffect(() => {
    if (!editing) {
      setDraft(name);
    }
  }, [name, editing]);

  useEffect(() => {
    if (!editing) {
      return;
    }

    input.current?.focus();
    input.current?.select();
  }, [editing]);

  const commit = (): void => {
    const next = sanitizeCompetitorName(draft);
    const duplicate = taken.some((other) => sameName(other, next));

    if (next && !duplicate) {
      onCommit(next);
    }

    setEditing(false);
  };

  if (editing) {
    return (
      <input
        ref={input}
        class="boat-select-name-input"
        data-racer-name
        aria-label="Your name"
        value={draft}
        maxLength={MAX_COMPETITOR_NAME_LENGTH}
        size={Math.min(MAX_COMPETITOR_NAME_LENGTH, Math.max(draft.length, 4))}
        spellcheck={false}
        onInput={(event) => setDraft(event.currentTarget.value)}
        onBlur={() => {
          if (ignoreBlur.current) {
            ignoreBlur.current = false;
            return;
          }

          commit();
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            event.stopPropagation();
            event.currentTarget.blur();
            return;
          }

          if (event.key === 'Escape') {
            event.preventDefault();
            event.stopPropagation();
            ignoreBlur.current = true;
            setDraft(name);
            setEditing(false);
          }
        }}
      />
    );
  }

  return (
    <button
      type="button"
      class="boat-select-name"
      data-racer-name
      aria-label={`Edit name, ${name}`}
      {...startNavProps('name', 'racer-name', 'overlay')}
      onClick={() => setEditing(true)}
    >
      <span>{name}</span>
      <span class="boat-select-name-icon" aria-hidden="true">
        <Pencil size={16} strokeWidth={2.25} />
      </span>
    </button>
  );
}

function BoatCarousel({
  boats,
  selectedId,
  open,
  onSelect,
  onLockHelm,
}: {
  boats: readonly BoatSelectChoice[];
  selectedId: string | null;
  /** False while Hot Seat helm is still being chosen. */
  open: boolean;
  onSelect: (id: string) => void;
  /** A pointer click on a dim card locks the helm choice it was showing, then picks the card. */
  onLockHelm: () => void;
}): JSX.Element {
  const scroller = useRef<HTMLDivElement>(null);
  const [overflow, setOverflow] = useState(false);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(true);

  const measure = (): void => {
    const el = scroller.current;

    if (!el) {
      return;
    }

    const nextOverflow = el.scrollWidth > el.clientWidth + 1;
    setOverflow(nextOverflow);
    setAtStart(el.scrollLeft <= 1);
    setAtEnd(el.scrollLeft + el.clientWidth >= el.scrollWidth - 1);
  };

  useLayoutEffect(() => {
    measure();
    const el = scroller.current;

    if (!el) {
      return;
    }

    const observer = new ResizeObserver(measure);
    observer.observe(el);
    el.addEventListener('scroll', measure, { passive: true });

    return () => {
      observer.disconnect();
      el.removeEventListener('scroll', measure);
    };
  }, [boats]);

  useEffect(() => {
    const el = scroller.current;

    if (!el || selectedId === null) {
      return;
    }

    const card = el.querySelector<HTMLElement>(
      `[data-boat-id="${CSS.escape(selectedId)}"]`,
    );
    card?.scrollIntoView({ inline: 'nearest', block: 'nearest' });
  }, [selectedId, boats]);

  const step = (direction: -1 | 1): void => {
    const el = scroller.current;
    const card = el?.querySelector<HTMLElement>('.boat-select-card');

    if (!el || !card) {
      return;
    }

    const gap = Number.parseFloat(getComputedStyle(el).columnGap) || 12;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    el.scrollBy({
      left: direction * (card.offsetWidth + gap),
      behavior: reduce ? 'auto' : 'smooth',
    });
  };

  return (
    <div
      class="boat-select-carousel"
      data-overflow={overflow ? 'true' : undefined}
      data-boats-pending={open ? undefined : 'true'}
    >
      <button
        type="button"
        class="boat-select-scroll"
        data-boat-scroll="back"
        aria-label="Previous boats"
        hidden={!overflow}
        disabled={!open || atStart}
        onClick={() => step(-1)}
      >
        <ChevronLeft size={22} strokeWidth={2.25} aria-hidden="true" />
      </button>
      <div class="boat-select-scroller" ref={scroller} role="group" aria-label="Boats">
        {boats.map((choice) => {
          const pressed = choice.id === selectedId;
          const name = boatDisplayName(choice.boat.name, choice.id);

          return (
            <button
              key={choice.id}
              type="button"
              class="boat-select-card"
              data-boat-id={choice.id}
              data-selected={pressed ? 'true' : undefined}
              aria-pressed={pressed}
              // Dim while the helm is open, but still a click target: the keyboard locks the
              // helm with Enter first, a pointer locks it by picking a boat.
              tabIndex={open ? undefined : -1}
              {...(open
                ? startNavProps(
                    'cards',
                    `hull-${choice.id}`,
                    'overlay',
                    'horizontal',
                    'radio',
                  )
                : {})}
              onClick={() => {
                if (!open) {
                  onLockHelm();
                }

                onSelect(choice.id);
              }}
            >
              <BoatSideArt boat={choice.boat} />
              <span class="map-select-item-name">{name}</span>
              <BoatStats boat={choice.boat} />
            </button>
          );
        })}
      </div>
      <button
        type="button"
        class="boat-select-scroll"
        data-boat-scroll="forward"
        aria-label="Next boats"
        hidden={!overflow}
        disabled={!open || atEnd}
        onClick={() => step(1)}
      >
        <ChevronRight size={22} strokeWidth={2.25} aria-hidden="true" />
      </button>
    </div>
  );
}

/** A saved bot profile the Helm row offers after the built-in levels. */
export type HelmProfile = {
  id: string;
  label: string;
  knobs: BotProfileKnobs;
};

type HelmChoice = { id: string; label: string; controller: SeatController };

function helmChoices(profiles: readonly HelmProfile[]): HelmChoice[] {
  return [
    { id: 'human', label: 'Human', controller: 'human' },
    ...LIVE_BOT_LEVELS.map((level) => ({
      id: level,
      label: `${level.charAt(0).toUpperCase()}${level.slice(1)} bot`,
      controller: { bot: level } as SeatController,
    })),
    ...profiles.map((profile) => ({
      id: `profile-${profile.id}`,
      label: profile.label,
      controller: {
        bot: 'profile',
        id: profile.id,
        name: profile.label,
        knobs: { ...profile.knobs },
      } as SeatController,
    })),
  ];
}

function helmId(controller: SeatController): string {
  if (controller === 'human') {
    return 'human';
  }

  return controller.bot === 'profile' ? `profile-${controller.id}` : controller.bot;
}

function HelmPicker({
  controller,
  profiles,
  onChange,
}: {
  controller: SeatController;
  profiles: readonly HelmProfile[];
  onChange: (next: SeatController) => void;
}): JSX.Element {
  const current = helmId(controller);

  return (
    <div class="boat-select-helm" role="group" aria-label="Helm">
      {helmChoices(profiles).map((choice) => (
        <button
          key={choice.id}
          type="button"
          class="start-boats-option"
          data-helm={choice.id}
          aria-pressed={current === choice.id}
          {...startNavProps(
            'helm',
            `helm-${choice.id}`,
            'overlay',
            'horizontal',
            'radio',
          )}
          onClick={() => onChange(choice.controller)}
        >
          {choice.label}
        </button>
      ))}
    </div>
  );
}

export function BoatSelect({
  boats,
  selectedId,
  seatLabel,
  controller,
  helmProfiles = [],
  helmLocked,
  onController,
  playerName,
  takenNames,
  onNameCommit,
  onSelect,
  onLockHelm,
  onConfirm,
  onDismiss,
}: {
  boats: readonly BoatSelectChoice[];
  selectedId: string | null;
  /** Hot Seat only. Time Trial leaves this unset. */
  seatLabel: string | null;
  /** Who sails this seat. Null hides the picker (Time Trial is always a human). */
  controller: SeatController | null;
  /** Saved bot profiles, offered after Easy–Expert. */
  helmProfiles?: readonly HelmProfile[];
  /**
   * Hot Seat: boats stay dim until Enter locks Human / bot, or a pointer picks a boat (which
   * locks the helm it was showing). Time Trial is always open.
   */
  helmLocked: boolean;
  onController: (next: SeatController) => void;
  playerName: string;
  /** Names already taken by the other seats. */
  takenNames: readonly string[];
  onNameCommit: (name: string) => void;
  onSelect: (id: string) => void;
  /** Hot Seat: lock the current helm choice (a pointer picked a boat while it was open). */
  onLockHelm: () => void;
  onConfirm: (choice: BoatSelectChoice) => void;
  onDismiss: () => void;
}): JSX.Element {
  const selected = boats.find((entry) => entry.id === selectedId) ?? null;
  const boatsOpen = controller === null || helmLocked;

  return (
    <div
      class="boat-select-backdrop"
      onClick={(event) => {
        if (event.target === event.currentTarget) {
          onDismiss();
        }
      }}
    >
      <div
        class="boat-select-box"
        role="dialog"
        aria-modal="true"
        aria-labelledby="boat-select-title"
        data-boat-select
        data-boats-pending={boatsOpen ? undefined : 'true'}
      >
        <header class="boat-select-heading">
          {seatLabel ? (
            <p class="boat-select-seat" data-boat-seat>
              {seatLabel}
            </p>
          ) : null}
          <RacerName
            key={seatLabel ?? 'solo'}
            name={playerName}
            taken={takenNames}
            onCommit={onNameCommit}
          />
          {controller ? (
            <HelmPicker controller={controller} profiles={helmProfiles} onChange={onController} />
          ) : null}
          <h2 id="boat-select-title" class="boat-select-title">
            Choose a boat
          </h2>
        </header>
        <BoatCarousel
          boats={boats}
          selectedId={selectedId}
          open={boatsOpen}
          onSelect={onSelect}
          onLockHelm={onLockHelm}
        />
        <button
          type="button"
          class="start-boats-option"
          data-boat-confirm
          disabled={!boatsOpen || selected === null}
          {...(boatsOpen && selected
            ? startNavProps('confirm', 'boat-confirm', 'overlay')
            : {})}
          onClick={() => {
            if (selected) {
              onConfirm(selected);
            }
          }}
        >
          Confirm
        </button>
      </div>
    </div>
  );
}
